import React, { useState, useEffect, useCallback } from 'react';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '@/lib/stripe';
import { CreditCard, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { reserveUrl } from '@/lib/apiUrls';
import { mergeReserveActAsHeaders, mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { formatStripeErrorMessage } from '@/lib/stripeErrors';
import { toast } from 'sonner';
import { fmtCurrency } from '@/utils/money';

function newShortfallIdempotencyKey() {
  const rnd = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `shortfall-${rnd}`;
}

/** Best-effort intent stamp. We never block the happy path on this — the resume endpoint
 *  can reconstruct state from Stripe + reserve_ledger if these calls fail. */
async function stampBookingIntent(intentId, patch, accessToken) {
  if (!intentId) return;
  try {
    await fetch(`/api/reserve/booking-intent/${intentId}/update`, {
      method: 'POST',
      // Act-as: the intent belongs to the customer (Review creates it with the same header).
      headers: mergeReserveActAsHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` }),
      body: JSON.stringify(patch),
    });
  } catch (_) { /* non-fatal */ }
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: '500',
      '::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
    },
    invalid: {
      color: '#f87171',
      iconColor: '#f87171',
    },
  },
  hidePostalCode: true,
};

function ReserveShortfallFormInner({
  reserveShortfall,
  onPaymentComplete,
  onError,
  onStart,
  isProcessing,
  disabled,
  termsAccepted,
  ensureIntentId,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [loadingMethods, setLoadingMethods] = useState(true);

  useEffect(() => {
    if (reserveShortfall <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch(reserveUrl('payment-methods'), {
          headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
        });
        const data = await res.json();
        if (cancelled) return;
        setPaymentMethods(data.payment_methods || []);
        const defaultPm = data.payment_methods?.find((p) => p.is_default);
        if (defaultPm) setSelectedCardId(defaultPm.id);
        else if (data.payment_methods?.length > 0) setSelectedCardId(data.payment_methods[0].id);
        if (!data.payment_methods?.length) setUseNewCard(true);
      } catch (e) {
        if (!cancelled) setPaymentMethods([]);
      } finally {
        if (!cancelled) setLoadingMethods(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reserveShortfall]);

  const handlePayAndBook = useCallback(async () => {
    if (!termsAccepted || isProcessing || reserveShortfall <= 0) return;
    if (!useNewCard && !selectedCardId && paymentMethods.length > 0) {
      toast.error('Please select a payment method');
      return;
    }
    if (useNewCard && (!stripe || !elements)) {
      toast.error('Payment system not ready');
      return;
    }

    onStart?.();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to continue');
        return;
      }

      // STEP 0: create the reserve_booking_intents row BEFORE money moves. Its UUID is
      // the idempotency key for Stripe, the Reserve debit (which the server prices from
      // this intent) and the bookings row, so partial failures resume without
      // double-charging or stranding the customer. Without an intent the booking can't
      // be debited, so the card isn't charged either.
      let intentId = null;
      try {
        intentId = ensureIntentId ? await ensureIntentId() : null;
      } catch (intentErr) {
        if (typeof console !== 'undefined') console.warn('[Shortfall] ensureIntentId threw', intentErr?.message);
        intentId = null;
      }
      if (ensureIntentId && !intentId) {
        const msg = "We couldn't start your checkout. Please try again.";
        toast.error(msg);
        onError?.(new Error(msg));
        return;
      }
      const stripeIdempotencyKey = intentId || newShortfallIdempotencyKey();

      const res = await fetch(reserveUrl('create-payment-intent'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        }),
        body: JSON.stringify({
          amount_cents: reserveShortfall,
          payment_method_id: useNewCard ? undefined : selectedCardId,
          description: 'Reserve Top-Up (booking shortfall)',
          // Use intent_id when available so retries/resumes converge; otherwise fall back
          // to a fresh random key so Stripe still dedupes same-request double submits.
          client_request_id: stripeIdempotencyKey,
        }),
      });
      const piData = await res.json().catch(() => ({}));
      if (!res.ok || piData.success === false) {
        const msg = formatStripeErrorMessage(piData) || piData.error || 'Failed to create payment';
        toast.error(msg);
        onError?.(new Error(msg));
        return;
      }

      // Record the PI on the intent as soon as it exists. If the tab closes right here,
      // the resume endpoint can look the PI up on Stripe to decide what happened.
      if (piData.payment_intent_id) {
        await stampBookingIntent(intentId, {
          stripe_payment_intent_id: piData.payment_intent_id
        }, session.access_token);
      }

      const postConfirm = () => fetch(reserveUrl('confirm-payment'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        }),
        body: JSON.stringify({ payment_intent_id: piData.payment_intent_id }),
      });

      const finish = async () => {
        await postConfirm();
        await stampBookingIntent(intentId, {
          status: 'charged',
          stripe_charge_at: new Date().toISOString()
        }, session.access_token);
        onPaymentComplete?.(reserveShortfall, intentId);
      };

      if (piData.status === 'succeeded') {
        await finish();
        return;
      }

      // Client-side confirmation required (new card, or 3DS for a saved card).
      if (useNewCard) {
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          toast.error('Card input not found. Please enter your card details.');
          onError?.(new Error('Card input not found'));
          return;
        }
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(piData.client_secret, {
          payment_method: { card: cardElement },
          setup_future_usage: 'off_session',
        });
        if (stripeError) {
          const msg = formatStripeErrorMessage(stripeError) || stripeError.message;
          toast.error(msg);
          onError?.(stripeError);
          return;
        }
        if (paymentIntent?.status !== 'succeeded') {
          toast.error('Payment was not completed. Please try a different card.');
          onError?.(new Error('Payment not completed'));
          return;
        }
      } else if (piData.status === 'requires_action' && piData.client_secret) {
        // Saved card needs 3DS challenge. handleCardAction avoids re-attaching the PM.
        const { error: actionError, paymentIntent } = await stripe.handleCardAction(piData.client_secret);
        if (actionError) {
          const msg = formatStripeErrorMessage(actionError) || actionError.message;
          toast.error(msg);
          onError?.(actionError);
          return;
        }
        if (paymentIntent?.status !== 'succeeded') {
          toast.error('Authentication was not completed. Please try again or use a different card.');
          onError?.(new Error('3DS incomplete'));
          return;
        }
      } else {
        // requires_payment_method / canceled / processing — the card was rejected or pending.
        toast.error('Your card could not be charged. Please try a different payment method.');
        onError?.(new Error('Card rejected'));
        return;
      }

      await finish();
    } catch (err) {
      toast.error(err.message || 'Payment failed');
      onError?.(err);
    }
  }, [
    reserveShortfall,
    useNewCard,
    selectedCardId,
    paymentMethods.length,
    stripe,
    elements,
    termsAccepted,
    isProcessing,
    onPaymentComplete,
    onError,
    onStart,
    ensureIntentId,
  ]);

  if (loadingMethods) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full max-w-2xl mx-auto">
      <div className={paymentCardGradient.container}>
        <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-4">Payment method</p>
        {paymentMethods.length > 0 && !useNewCard ? (
          <div className="space-y-2.5">
            {paymentMethods.map((pm) => (
              <button
                key={pm.id}
                type="button"
                onClick={() => setSelectedCardId(pm.id)}
                className={`${paymentCardGradient.cardRowBase} ${
                  selectedCardId === pm.id ? paymentCardGradient.cardSelected : paymentCardGradient.cardUnselected
                }`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                  selectedCardId === pm.id ? paymentCardGradient.iconSelected : paymentCardGradient.iconUnselected
                }`}>
                  <CreditCard className={`w-5 h-5 ${selectedCardId === pm.id ? paymentCardGradient.iconColorSelected : paymentCardGradient.iconColorUnselected}`} />
                </div>
                <div className="flex-1 text-left">
                  <span className="font-medium text-white capitalize">{pm.brand}</span>
                  <span className="text-white/50 font-mono text-sm ml-2">•••• {pm.last4}</span>
                </div>
                {selectedCardId === pm.id && (
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${paymentCardGradient.checkBadge}`}>
                    <Check className={`w-3.5 h-3.5 ${paymentCardGradient.checkIcon}`} strokeWidth={2.5} />
                  </div>
                )}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setUseNewCard(true)}
              className={paymentCardGradient.addNewCard}
            >
              <div className={paymentCardGradient.addNewCardIcon}>
                <CreditCard className="w-5 h-5 text-white/40" />
              </div>
              Use a new card
            </button>
          </div>
        ) : (
          <div className="space-y-4 min-w-0">
            <div className={paymentCardGradient.cardElementWrapper}>
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>
            {paymentMethods.length > 0 && (
              <button
                type="button"
                onClick={() => { setUseNewCard(false); setSelectedCardId(paymentMethods[0]?.id); }}
                className={paymentCardGradient.useSavedCardLink}
              >
                ← Use saved card instead
              </button>
            )}
          </div>
        )}
      </div>

      <Button
        type="button"
        onClick={handlePayAndBook}
        disabled={
          disabled ||
          isProcessing ||
          (!useNewCard && !selectedCardId && paymentMethods.length > 0) ||
          (useNewCard && (!stripe || !elements))
        }
        className="w-full bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 font-normal disabled:opacity-50 disabled:cursor-not-allowed"
        size="lg"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-4 h-4 mr-2" />
            Pay ${fmtCurrency(reserveShortfall)} & Book
          </>
        )}
      </Button>
    </div>
  );
}

export default function ReserveShortfallPaymentForm(props) {
  if (!stripePromise) return null;
  return (
    <Elements stripe={stripePromise}>
      <ReserveShortfallFormInner {...props} />
    </Elements>
  );
}
